import { useEffect, useRef, useState } from "react";
import { useSeo } from "@/utils/seo";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight, FileDown, Search, X, ZoomIn } from "lucide-react";
import { PageFlip } from "page-flip";
import { Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const VARIANTS = {
    public: {
        count: 62,
        folder: "/katalogseiten",
        pdf: "/datenblaetter/katalog_ohne_preise.pdf",
        title: { de: "Fachhandelskatalog online blättern.", en: "Browse the trade catalogue online." },
        crumb: { de: "Pageflip-Katalog", en: "Flip catalogue" },
        searchPlaceholder: { de: "Im Katalog suchen — Typ, Artikel oder Art.-Nr.", en: "Search the catalogue — type, item or article no." },
        testid: "pageflip",
    },
    preise: {
        count: 62,
        folder: "/katalogseiten-preise",
        pdf: "/datenblaetter/katalog_preise.pdf",
        title: { de: "Katalog & Händlerpreise online blättern.", en: "Catalogue & dealer prices — browse online." },
        crumb: { de: "Pageflip-Katalog Preise", en: "Flip catalogue — prices" },
        searchPlaceholder: { de: "Im Preiskatalog suchen — Typ, Artikel oder Art.-Nr.", en: "Search the price catalogue — type, item or article no." },
        testid: "pageflip-preise",
    },
};

export default function FlipbookPage({ preise = false }) {
    const v = VARIANTS[preise ? "preise" : "public"];
    const { t } = useLang();
    useSeo(
        preise
            ? t("Händlerpreise: Katalog & Preisliste | Müller", "Dealer prices: catalogue & price list | Müller")
            : t("Fachhandelskatalog online blättern | Müller Hemhofen", "Trade catalogue — browse online | Müller Hemhofen"),
        preise
            ? t("Katalog & Händlerpreise für Fachpartner — Zugang nur mit Kundennummer.", "Catalogue & dealer prices for trade partners — access only with a customer number.")
            : t(
                "Fachhandelskatalog von Müller Satelliten- und Antennentechnik online durchblättern: Kopfstationen, Verteiltechnik, Antennen und Zubehör — ohne Preise.",
                "Browse the Müller trade catalogue online: headends, distribution technology, antennas and accessories — without prices."
            ),
        preise,
    );
    const COUNT = v.count;
    const IMAGES = Array.from({ length: COUNT }, (_, i) => `${v.folder}/page-${i + 1}.jpg?v=3`);
    const wrapRef = useRef(null);
    const instRef = useRef(null);
    const searchRef = useRef(null);
    const indexRef = useRef(null);
    const [page, setPage] = useState(1);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState(null);
    const [open, setOpen] = useState(false);
    const [zoomOpen, setZoomOpen] = useState(false);
    const [zoomPage, setZoomPage] = useState(1);

    useEffect(() => {
        if (!zoomOpen) return;
        const onKey = (e) => {
            if (e.key === "Escape") closeZoom();
            if (e.key === "ArrowLeft") stepZoom(-1);
            if (e.key === "ArrowRight") stepZoom(1);
        };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [zoomOpen, zoomPage]);

    const stepZoom = (dir) => {
        setZoomPage((p) => Math.min(COUNT, Math.max(1, p + dir)));
        setZoomScale(1);
    };

    // Zoom in der Leseansicht: 2-Finger-Ziehen = stufenlos, Tippen = Stufen, Wischen = Blaettern
    const [zoomScale, setZoomScale] = useState(1);
    const pinchRef = useRef({ dist: 0, startScale: 1, multi: false });
    const touchX = useRef(null);
    const lastTouchEnd = useRef(0);

    const cycleZoom = () => {
        setZoomScale((s) => (s >= 3 ? 1 : s >= 2 ? 3.5 : 2));
    };

    const onTouchStart = (e) => {
        if (e.touches.length === 2) {
            const [a, b] = e.touches;
            pinchRef.current.dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
            pinchRef.current.startScale = zoomScale;
            pinchRef.current.multi = true;
        } else if (e.touches.length === 1) {
            touchX.current = e.touches[0].clientX;
            pinchRef.current.multi = false;
        }
    };
    const onTouchMove = (e) => {
        if (e.touches.length === 2 && pinchRef.current.dist) {
            const [a, b] = e.touches;
            const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
            setZoomScale(Math.min(5, Math.max(1, pinchRef.current.startScale * (d / pinchRef.current.dist))));
        }
    };
    const onTouchEnd = (e) => {
        if (pinchRef.current.multi) {
            pinchRef.current.dist = 0;
            return;
        }
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) >= 60) {
            if (zoomScale === 1) stepZoom(dx < 0 ? 1 : -1);
        } else if (Math.abs(dx) < 10) {
            cycleZoom();
        }
    };

    const closeZoom = () => {
        setZoomOpen(false);
        setZoomScale(1);
        if (instRef.current) {
            try {
                instRef.current.turnToPage(zoomPage - 1);
            } catch (e) {
                /* ignore */
            }
        }
        setPage(zoomPage);
    };

    useEffect(() => {
        fetch(`${v.folder}/search-index.json`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (d && d.pages) indexRef.current = d;
            })
            .catch(() => {});
    }, [v.folder]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    const runSearch = (value) => {
        setQuery(value);
        const term = value.trim().toLowerCase();
        if (term.length < 2 || !indexRef.current) {
            setResults(null);
            setOpen(false);
            return;
        }
        const pages = indexRef.current.pages || {};
        const out = [];
        for (let p = 1; p <= COUNT; p++) {
            const text = pages[String(p)] || "";
            const lower = text.toLowerCase();
            const first = lower.indexOf(term);
            if (first !== -1) {
                const count = lower.split(term).length - 1;
                const snippet = text
                    .slice(Math.max(0, first - 45), first + 75)
                    .replace(/\s+/g, " ")
                    .trim();
                out.push({ page: p, count, snippet });
            }
        }
        out.sort((a, b) => b.count - a.count || a.page - b.page);
        setResults(out.slice(0, 8));
        setOpen(true);
    };

    const jumpTo = (p) => {
        if (instRef.current) {
            instRef.current.turnToPage(p - 1);
            setPage(p);
        }
        setOpen(false);
        const book = document.querySelector('[data-testid="pageflip-book"]');
        if (!book) return;
        const y = Math.max(0, book.getBoundingClientRect().top + window.scrollY - 120);
        if (window.__lenis) window.__lenis.scrollTo(y, { duration: 0.8 });
        else book.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const clearSearch = () => {
        setQuery("");
        setResults(null);
        setOpen(false);
    };

    const highlight = (snippet) => {
        const term = query.trim();
        if (!term) return snippet;
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return snippet.split(new RegExp(`(${escaped})`, "ig")).map((part, i) =>
            part.toLowerCase() === term.toLowerCase() ? (
                <mark key={i} className="bg-red-100 text-red-800 rounded px-0.5">
                    {part}
                </mark>
            ) : (
                <span key={i}>{part}</span>
            )
        );
    };


    const [isSmall, setIsSmall] = useState(() => window.matchMedia("(max-width: 767px)").matches);
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)");
        const onChange = (e) => setIsSmall(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap || isSmall || instRef.current) return;
        wrap.innerHTML = "";
        const book = document.createElement("div");
        wrap.appendChild(book);
        const pf = new PageFlip(book, {
            width: 880,
            height: 1245,
            size: "stretch",
            minWidth: 290,
            maxWidth: 1760,
            maxShadowOpacity: 0.5,
            showCover: true,
            mobileScrollSupport: true,
            usePortrait: true,
        });
        pf.loadFromImages(IMAGES);
        pf.on("flip", (e) => {
            const p = typeof e?.data === "number" ? e.data : (pf.getCurrentPageIndex ? pf.getCurrentPageIndex() : 0);
            setPage(p + 1);
        });
        instRef.current = pf;
        return () => {
            try {
                pf.destroy();
            } catch (e) {
                /* ignore */
            }
            instRef.current = null;
        };
    }, [isSmall]);

    const prev = () => (isSmall ? (setPage((p) => Math.max(1, p - 1)), setReaderScale(1)) : instRef.current && instRef.current.flipPrev());
    const next = () => (isSmall ? (setPage((p) => Math.min(COUNT, p + 1)), setReaderScale(1)) : instRef.current && instRef.current.flipNext());

    // Handy-Lesemodus: ganze Seite in Bildschirmbreite, gleiche Gesten wie die Leseansicht
    const [readerScale, setReaderScale] = useState(1);
    useEffect(() => setReaderScale(1), [page]);
    const readerX = useRef(null);
    const readerPinch = useRef({ dist: 0, startScale: 1, multi: false });
    const readerLastTouch = useRef(0);

    const readerTouchStart = (e) => {
        if (e.touches.length === 2) {
            const [a, b] = e.touches;
            readerPinch.current.dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
            readerPinch.current.startScale = readerScale;
            readerPinch.current.multi = true;
        } else if (e.touches.length === 1) {
            readerX.current = e.touches[0].clientX;
            readerPinch.current.multi = false;
        }
    };
    const readerTouchMove = (e) => {
        if (e.touches.length === 2 && readerPinch.current.dist) {
            const [a, b] = e.touches;
            const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
            setReaderScale(Math.min(5, Math.max(1, readerPinch.current.startScale * (d / readerPinch.current.dist))));
        }
    };
    const readerTouchEnd = (e) => {
        if (readerPinch.current.multi) {
            readerPinch.current.dist = 0;
            return;
        }
        if (readerX.current === null) return;
        const dx = e.changedTouches[0].clientX - readerX.current;
        readerX.current = null;
        if (Math.abs(dx) >= 60) {
            if (readerScale === 1) setPage((p) => Math.min(COUNT, Math.max(1, p + (dx < 0 ? 1 : -1))));
            setReaderScale(1);
        } else if (Math.abs(dx) < 10) {
            setReaderScale((s) => (s >= 3 ? 1 : s >= 2 ? 3.5 : 2));
        }
    };

    return (
        <main data-testid={v.testid} className="pt-24 sm:pt-28 pb-16 sm:pb-20">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12">
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400" data-testid="breadcrumb">
                    <Link to="/downloads" data-testid="breadcrumb-downloads" className="hover:text-red-700 transition-colors">
                        {t("Download", "Downloads")}
                    </Link>
                    <span className="mx-2">/</span>
                    <span className="text-slate-600">{t(v.crumb.de, v.crumb.en)}</span>
                </p>
                <div className="mt-4 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                    <div>
                        <Eyebrow>{t("Katalog", "Catalogue")}</Eyebrow>
                        <h1 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                            {t(v.title.de, v.title.en)}
                        </h1>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2" data-testid="pageflip-controls">
                        <button
                            onClick={prev}
                            data-testid="pageflip-prev"
                            aria-label={t("Seite zurück", "Previous page")}
                            className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-slate-300 bg-white hover:border-red-400 hover:text-red-700 text-slate-600 transition-colors"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <span className="font-mono text-xs text-slate-500 px-2 min-w-[90px] text-center" data-testid="pageflip-counter">
                            {t("Seite", "Page")} {page} / {COUNT}
                        </span>
                        <button
                            onClick={next}
                            data-testid="pageflip-next"
                            aria-label={t("Seite weiter", "Next page")}
                            className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-red-700 hover:bg-red-800 text-white transition-colors"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                        <button
                            onClick={() => {
                                setZoomPage(page);
                                setZoomScale(1);
                                setZoomOpen(true);
                            }}
                            data-testid="pageflip-zoom-open"
                            aria-label={t("Seite vergrößern", "Enlarge page")}
                            className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-slate-300 hover:border-red-400 hover:text-red-700 text-slate-700 text-sm font-semibold px-4 py-2.5 transition-colors"
                        >
                            <ZoomIn className="h-4 w-4" />
                            {t("Vergrößern", "Enlarge")}
                        </button>
                        <a
                            href={v.pdf}
                            data-testid="pageflip-download"
                            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 hover:border-red-400 hover:text-red-700 text-slate-700 text-sm font-semibold px-4 py-2.5 transition-colors"
                        >
                            <FileDown className="h-4 w-4" />
                            PDF
                        </a>
                    </div>
                </div>
                <div className="relative mt-6 max-w-md mx-auto z-30" ref={searchRef} data-testid="pageflip-search">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            type="search"
                            value={query}
                            onChange={(e) => runSearch(e.target.value)}
                            onFocus={() => results && setOpen(true)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && results && results.length) jumpTo(results[0].page);
                                if (e.key === "Escape") setOpen(false);
                            }}
                            placeholder={t(v.searchPlaceholder.de, v.searchPlaceholder.en)}
                            aria-label="Katalog durchsuchen"
                            data-testid="pageflip-search-input"
                            className="w-full rounded-full border border-slate-300 bg-white py-3 pl-11 pr-11 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-colors [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
                        />
                        {query && (
                            <button
                                onClick={clearSearch}
                                data-testid="pageflip-search-clear"
                                aria-label="Suche löschen"
                                className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-7 w-7 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    {open && results && (
                        <div
                            data-testid="pageflip-search-results"
                            className="absolute left-0 right-0 mt-2 rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden z-30"
                        >
                            {results.length === 0 ? (
                                <p className="px-4 py-4 text-sm text-slate-500" data-testid="pageflip-search-empty">
                                    {t("Keine Treffer für", "No matches for")} „{query}" — {t("bitte anderen Suchbegriff probieren.", "please try another search term.")}
                                </p>
                            ) : (
                                <ul className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                                    {results.map((r, i) => (
                                        <li key={r.page}>
                                            <button
                                                onClick={() => jumpTo(r.page)}
                                                data-testid={`pageflip-search-result-${i}`}
                                                className="w-full text-left px-4 py-3 hover:bg-red-50/60 transition-colors"
                                            >
                                                <span className="block font-mono text-[10px] uppercase tracking-widest text-red-700">
                                                    {t("Seite", "Page")} {r.page} · {r.count} {t("Treffer", "hits")}
                                                </span>
                                                <span className="mt-0.5 block text-sm text-slate-700 leading-snug">
                                                    …{highlight(r.snippet)}…
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-8 px-4 sm:px-6 flex justify-center overflow-x-clip">
                {isSmall ? (
                    <div
                        data-testid="pageflip-mobile-reader"
                        className="w-full max-w-[720px]"
                        onTouchStart={readerTouchStart}
                        onTouchMove={readerTouchMove}
                        onTouchEnd={readerTouchEnd}
                    >
                        <div className="overflow-auto [touch-action:pan-x_pan-y]">
                            <img
                                src={`${v.folder}/page-${page}.jpg?v=3`}
                                data-testid="pageflip-mobile-reader-img"
                                alt={`Katalogseite ${page}`}
                                className="block max-w-none rounded shadow-lg cursor-zoom-in"
                                style={{ width: `max(100%, ${Math.round(readerScale * 100)}%)`, height: "auto", margin: "0 auto" }}
                                onClick={() => {
                                    if (Date.now() - readerLastTouch.current > 500)
                                        setReaderScale((s) => (s >= 3 ? 1 : s >= 2 ? 3.5 : 2));
                                }}
                                onTouchEnd={() => (readerLastTouch.current = Date.now())}
                            />
                        </div>
                        <p className="mt-2 text-center text-[11px] text-slate-400">
                            {t("Zum Lesen: mit zwei Fingern ziehen oder Bild antippen · wischen = blättern", "To read: pinch with two fingers or tap the image · swipe to turn pages")}
                        </p>
                    </div>
                ) : (
                    <div ref={wrapRef} data-testid="pageflip-book" className="w-full max-w-[1760px]" />
                )}
            </div>
            {zoomOpen && (
                <div
                    data-testid="pageflip-zoom-overlay"
                    className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm flex flex-col"
                    onClick={closeZoom}
                >
                    <div
                        className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 text-white shrink-0"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col">
                            <span className="font-mono text-xs text-white/80" data-testid="pageflip-zoom-counter">
                                {t("Seite", "Page")} {zoomPage} / {COUNT}
                            </span>
                            <span className="text-[11px] text-white/50 sm:hidden">
                                {t("Zum Lesen: mit zwei Fingern ziehen oder Bild antippen · wischen = blättern", "To read: pinch with two fingers or tap the image · swipe to turn pages")}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => stepZoom(-1)}
                                data-testid="pageflip-zoom-prev"
                                aria-label={t("Seite zurück", "Previous page")}
                                disabled={zoomPage <= 1}
                                className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/30 text-white hover:bg-white/10 disabled:opacity-30 transition-colors"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                                onClick={() => stepZoom(1)}
                                data-testid="pageflip-zoom-next"
                                aria-label={t("Seite weiter", "Next page")}
                                disabled={zoomPage >= COUNT}
                                className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-red-700 hover:bg-red-800 text-white disabled:opacity-30 transition-colors"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                            <button
                                onClick={closeZoom}
                                data-testid="pageflip-zoom-close"
                                aria-label={t("Vergrößerung schließen", "Close enlarged view")}
                                className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                    <div
                        className="flex-1 min-h-0 overflow-auto px-4 pb-6 [touch-action:pan-x_pan-y]"
                        onClick={(e) => e.stopPropagation()}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <img
                            src={`${v.folder}/page-${zoomPage}.jpg?v=3`}
                            data-testid="pageflip-zoom-img"
                            alt={`Katalogseite ${zoomPage} in Vollbild`}
                            className="mx-auto block max-w-none rounded shadow-2xl cursor-zoom-in"
                            style={{ width: `max(100%, ${Math.round(zoomScale * 100)}%)`, height: "auto" }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (Date.now() - lastTouchEnd.current > 500) cycleZoom();
                            }}
                        />
                    </div>
                </div>
            )}
            <div className="mt-8 text-center">
                <Link
                    to="/downloads"
                    data-testid="back-to-downloads"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-red-700 transition-colors"
                >
                    <ArrowUpRight className="h-4 w-4 rotate-180" />
                    {t("Zurück zu allen Downloads", "Back to all downloads")}
                </Link>
            </div>
        </main>
    );
}
